import { isNullOrUndefined } from '@deltasierra/type-utilities';
import { lastIndexOf } from '@deltasierra/utilities-object';
import { getRectangleContaining } from '../../geometry';
import { getLineHeightInPixels, wrapInContextTransaction } from '../canvasUtils';
import { IRenderContext } from '../renderContext';
import { convertUnits, POINTS_PER_INCH, PrintUnits } from '../../builderDocument';
import {
    IToken,
    ITokenIterator,
    IRenderResult,
    IRenderTextShrinkOptions,
    ITextRenderOptions,
    IRendererState,
    UnexpectedTokenType,
    renderText,
    defaultLineHeight,
    HorizontalTextAlignment,
    VerticalTextAlignment,
    letterSpacingCharacter,
} from './common';

class UnsupportedOperationError extends Error {}

export type SupportedFontSizeUnit = 'pt' | 'px';

enum PlainTextTokenType {
    Word = 'Word' as any,
    Tab = 'Tab' as any,
    Space = 'Space' as any,
    NewLine = 'NewLine' as any,
}

interface IPlainTextToken extends IToken {
    completeText: string;
    index: number;
    totalLength: number;
    type: PlainTextTokenType;
}

interface IMeasuredPlainTextToken extends IPlainTextToken {
    width: number;
}

class PlainTextLine {
    public tokens: IMeasuredPlainTextToken[] = [];

    public width = 0;

    public wordWidth = 0;

    public spaceWidth = 0;

    public trailingSpaceWidth = 0;

    public isWrapped = false;
}

interface IPlainTextRenderState extends IRendererState {
    currentLine: PlainTextLine;
    lineHeight: number;
    lines: PlainTextLine[];
}

export interface IPlainTextRenderOptions extends ITextRenderOptions {
    fallbackFontFamily?: string;
    fillColor?: string;
    strokeColor?: string;
    strokeWidth?: number;
    letterSpacing?: number;
    useOldTextRenderingLineHeightFix: boolean;
    useTextRenderingLineHeightFix: boolean;
}

function getRegexWithLetterSpacingCharacter(reg: RegExp): RegExp {
    const expression = reg.source;
    return new RegExp(`${letterSpacingCharacter}*${expression}${letterSpacingCharacter}*`);
}

function getTokenType(text: string): PlainTextTokenType {
    switch (true) {
        case getRegexWithLetterSpacingCharacter(/\u0020/).test(text):
            return PlainTextTokenType.Space;

        case getRegexWithLetterSpacingCharacter(/\t/).test(text):
            return PlainTextTokenType.Tab;

        case getRegexWithLetterSpacingCharacter(/(?:\n|\r|\n\r|\r\n)/).test(text):
            return PlainTextTokenType.NewLine;

        default:
            return PlainTextTokenType.Word;
    }
}

function parsePlainText(text: string): ITokenIterator<IPlainTextToken> {
    /*
     * Split text up into an array where each element (token) will be either:
     *  - a single space
     *  - a single tab character
     *  - a single new line
     *  - a word (i.e. string of sequentials character that don't match any of the above)
     */
    const tokens = text
        .trim()
        // .split(/(\u200A*(?:\u0020+|\t|(?:\n|\r|\n\r|\r\n))\u200A*)/gi)
        .split(
            new RegExp(
                `(${letterSpacingCharacter}*(?:\u0020+|\t|(?:\n|\r|\n\r|\r\n))${letterSpacingCharacter}*)`,
                'gi',
            ),
        )
        .filter(tokenText => tokenText.length > 0)
        .map((tokenText, index, arr) => ({
            completeText: text,
            index,
            text: tokenText,
            totalLength: arr.length,
            type: getTokenType(tokenText),
        }));

    let tokenIndex = -1;

    return (): IPlainTextToken | null => {
        if (tokenIndex + 1 > tokens.length) {
            // Return null to indicate that we're at the end
            return null;
        }

        return tokens[++tokenIndex];
    };
}

const getTokenWidth = (context: IRenderContext, options: { tabText?: string }, token: IPlainTextToken): number => {
    const isLastToken = token.index === token.totalLength - 1;
    const completeText = token.completeText;
    switch (token.type) {
        case PlainTextTokenType.Space:
        case PlainTextTokenType.Word:
            return context.textWidth(token.text, { completeText, isLastToken });

        case PlainTextTokenType.Tab:
            if (!options.tabText) {
                throw new UnsupportedOperationError('When token type is "Tab", tabText must be supplied in options.');
            }
            return context.textWidth(options.tabText, { completeText, isLastToken });

        case PlainTextTokenType.NewLine:
            return 0;

        default:
            throw new UnexpectedTokenType(token);
    }
};

function initState(context: IRenderContext, state: IRendererState, options: ITextRenderOptions): IPlainTextRenderState {
    // Initialise any plain text specific state data
    const lineHeightMultiplier = options.font.lineHeight || defaultLineHeight;
    context.fontFamily(options.font.family);
    context.fontSize(options.font.size, options.font.sizeUnit);

    let lineHeight = 0;
    if (options.useTextRenderingLineHeightFix) {
        lineHeight = getLineHeightInPixels(context.lineHeight(options.fallbackFontFamily), 'px', lineHeightMultiplier);
    } else if (options.useOldTextRenderingLineHeightFix) {
        lineHeight = getLineHeightInPixels(
            context.textHeight('Mg', { fallbackFontFamily: options.fallbackFontFamily }),
            'px',
            lineHeightMultiplier,
        );
    } else {
        lineHeight = getLineHeightInPixels(options.font.size, options.font.sizeUnit, lineHeightMultiplier);
    }

    return {
        ...state,
        currentLine: new PlainTextLine(),
        lineHeight,
        lines: [],
    };
}

function processCurrentLine(state: IPlainTextRenderState): void {
    state.lines.push(state.currentLine);

    state.currentLine = new PlainTextLine();

    state.lineCount++;
}

function getLineXPosition(
    lineWidth: number,
    horizontalAlignment: HorizontalTextAlignment | undefined,
    minX: number,
    maxX: number,
): number {
    switch (horizontalAlignment) {
        case HorizontalTextAlignment.Left:
        case HorizontalTextAlignment.Justify:
            return minX;

        case HorizontalTextAlignment.Center: {
            const maxWidth = maxX - minX;
            return minX + maxWidth / 2 - lineWidth / 2;
        }

        case HorizontalTextAlignment.Right:
            return maxX - lineWidth;

        default:
            throw new Error(`Unexpected HorizontalTextAlignment value: ${horizontalAlignment}`);
    }
}

function getLinesYPosition(
    lineCount: number,
    lineHeight: number,
    verticalAlignment: VerticalTextAlignment | undefined,
    minY: number,
    maxY: number,
): number {
    switch (verticalAlignment) {
        case VerticalTextAlignment.Top:
            return minY;

        case VerticalTextAlignment.Center:
            return minY + (maxY - minY) / 2 - (lineCount * lineHeight) / 2;

        case VerticalTextAlignment.Bottom:
            return maxY - lineCount * lineHeight;

        default:
            throw new Error(`Unexpected VerticalTextAlignment value: ${verticalAlignment}`);
    }
}

function renderLines(context: IRenderContext, options: IPlainTextRenderOptions, state: IPlainTextRenderState): void {
    let y = getLinesYPosition(
        state.lines.length,
        state.lineHeight,
        options.verticalAlignment,
        options.bounds.y,
        options.bounds.y + options.bounds.height,
    );

    for (const line of state.lines) {
        const horizontalAlignment =
            options.horizontalAlignment === HorizontalTextAlignment.Justify && !line.isWrapped
                ? HorizontalTextAlignment.Left
                : options.horizontalAlignment;

        renderLine(context, options, horizontalAlignment, line, y, state);

        y += state.lineHeight;
    }
}

// eslint-disable-next-line max-params
function renderLine(
    context: IRenderContext,
    options: IPlainTextRenderOptions,
    horizontalAlignment: HorizontalTextAlignment | undefined,
    line: PlainTextLine,
    y: number,
    state: IPlainTextRenderState,
) {
    const x = getLineXPosition(
        line.width - line.trailingSpaceWidth,
        options.horizontalAlignment,
        options.bounds.x,
        options.bounds.width,
    );

    let yRenderPosition = y;
    if (options.useTextRenderingLineHeightFix) {
        // DS-2925: The improved fix for the line heights
        const fontSizeInPt = convertUnits(options.font.size, options.font.sizeUnit as PrintUnits, PrintUnits.pixels);
        const resolution = 72;
        yRenderPosition =
            y +
            (options.fontMetrics.ascent * fontSizeInPt * resolution) /
                (POINTS_PER_INCH * options.fontMetrics.unitsPerEm);
    } else if (options.useOldTextRenderingLineHeightFix) {
        /**
         * DS-2647: Text rendering fix. This fix is mainly targeted at print templates using TextRenderingV2
         * but is needed here for old templates.
         */
        yRenderPosition = y + state.lineHeight / 2;
    }

    const lineBounds = {
        height: state.lineHeight,
        width: line.width - line.trailingSpaceWidth,
        x,
        y,
    };

    state.actualBounds = getRectangleContaining(state.actualBounds, lineBounds);

    if (!options.measureOnly) {
        if (horizontalAlignment !== HorizontalTextAlignment.Justify) {
            context.text({
                fill: true,
                position: { x, y: yRenderPosition },
                stroke: !!options.strokeWidth,
                text: line.tokens.map(token => token.text).join(''),
            });
        } else {
            renderLineJustified(context, options, line, x, y);
        }
    }
}

function getJustifiedLineSpaceMultiplier(options: IPlainTextRenderOptions, line: PlainTextLine): number {
    const totalAvailableSpaceWidth = options.bounds.width - line.wordWidth;
    const spaceMultiplier = totalAvailableSpaceWidth / (line.spaceWidth - line.trailingSpaceWidth);
    return spaceMultiplier;
}

function renderLineJustified(
    context: IRenderContext,
    options: IPlainTextRenderOptions,
    line: PlainTextLine,
    x: number,
    y: number,
) {
    const lastWordTokenIndex = lastIndexOf(line.tokens, token => token.type === PlainTextTokenType.Word);

    const spaceMultiplier = getJustifiedLineSpaceMultiplier(options, line);

    for (let index = 0; index <= lastWordTokenIndex; index++) {
        const token = line.tokens[index];

        if (token.type === PlainTextTokenType.Word) {
            context.text({
                fill: true,
                position: { x, y },
                stroke: !!options.strokeWidth,
                text: token.text,
            });
            // eslint-disable-next-line no-param-reassign
            x += token.width;
        } else {
            // eslint-disable-next-line no-param-reassign
            x += token.width * spaceMultiplier;
        }
    }
}

// eslint-disable-next-line max-statements, complexity
function processToken(
    context: IRenderContext,
    options: ITextRenderOptions,
    state: IPlainTextRenderState,
    token: IPlainTextToken | null,
): void {
    /*
     * If the token is null then we know we've reached the end
     * And should add the current line if there is one
     * And do our final render
     */
    if (isNullOrUndefined(token)) {

        if (state.currentLine && state.currentLine.tokens && state.currentLine.tokens.length) {
            processCurrentLine(state);
            renderLines(context, options, state);
        }
        return;
    }

    let tokenWidth = getTokenWidth(context, { tabText: options.tabText }, token);

    if (options.wordWrap && options.allowWordBreaks) {

        if (token.type === PlainTextTokenType.Word && options.allowWordBreaks && token.text.length > 1) {
            const truncatedToken: IPlainTextToken = {
                completeText: token.completeText,
                index: token.index,
                text: token.text,
                totalLength: token.totalLength,
                type: token.type,
            };

            let remainderToken: IPlainTextToken | null = null;

            /*
             * Is the current word more than one character long and wider than our max width all by itself?
             * If so, then we need to keep trimming characters off the end of the word until it can fit or
             * There is just one character left
             */
            while (tokenWidth > options.bounds.width && truncatedToken.text.length > 1) {
                truncatedToken.text = truncatedToken.text.substr(0, truncatedToken.text.length - 1);

                remainderToken = {
                    completeText: token.completeText,
                    index: token.index,
                    text: token.text.substr(truncatedToken.text.length),
                    totalLength: token.totalLength,
                    type: token.type,
                };

                tokenWidth = getTokenWidth(context, { tabText: options.tabText }, truncatedToken);
            }

            // Did we have to shrink the current word?
            if (remainderToken) {
                // Process the first part of the word
                state.currentLine.isWrapped = true;
                processToken(context, options, state, truncatedToken);

                // Now Process the remainder
                processToken(context, options, state, remainderToken);
                return;
            }
        }
    }

    switch (token.type) {
        case PlainTextTokenType.Word:
            if (options.wordWrap) {
                // Is the current line not empty and too long to render with the current word?
                if (

                    state.currentLine &&

                    state.currentLine.tokens &&
                    state.currentLine.tokens.length &&
                    state.currentLine.width + tokenWidth > options.bounds.width
                ) {
                    state.currentLine.isWrapped = true;
                    processCurrentLine(state);
                }
            }

            state.currentLine.tokens.push({
                completeText: token.completeText,
                index: token.index,
                text: token.text,
                totalLength: token.totalLength,
                type: token.type,
                width: tokenWidth,
            });
            state.currentLine.width += tokenWidth;
            state.currentLine.wordWidth += tokenWidth;
            state.currentLine.trailingSpaceWidth = 0;
            break;

        case PlainTextTokenType.Space:
        case PlainTextTokenType.Tab:
            state.currentLine.tokens.push(
                token.type === PlainTextTokenType.Tab
                    ? {
                          completeText: token.completeText,
                          index: token.index,
                          text: options.tabText!,
                          totalLength: token.totalLength,
                          type: PlainTextTokenType.Tab,
                          width: tokenWidth,
                      }
                    : {
                          completeText: token.completeText,
                          index: token.index,
                          text: token.text,
                          totalLength: token.totalLength,
                          type: token.type,
                          width: tokenWidth,
                      },
            );
            state.currentLine.width += tokenWidth;
            state.currentLine.spaceWidth += tokenWidth;
            state.currentLine.trailingSpaceWidth += tokenWidth;
            break;

        case PlainTextTokenType.NewLine:
            processCurrentLine(state);
            break;

        default:
            throw new UnexpectedTokenType(token);
    }
}

export function renderPlainText(
    context: IRenderContext,
    text: string,
    options: IPlainTextRenderOptions & IRenderTextShrinkOptions,
): IRenderResult {
    return wrapInContextTransaction(context, () => {
        if (!isNullOrUndefined(options.fillColor)) {
            context.fillColor(options.fillColor);
        }

        if (!isNullOrUndefined(options.strokeColor)) {
            context.strokeColor(options.strokeColor);
        }

        if (!isNullOrUndefined(options.strokeWidth)) {
            context.lineWidth(options.strokeWidth);
        }

        if (!isNullOrUndefined(options.letterSpacing)) {
            context.letterSpacing(options.letterSpacing);
        }

        return renderText(context, text, options, parsePlainText, processToken, initState);
    })();
}
